import { ChevronDownIcon } from '@heroicons/react/outline';
import { Badge, ProgressBar, Title } from '@yoast/ui-library';
import cx from 'classnames';

import type { QueryResultDetailedSentimentsItem } from '../../api/generated/insights.schemas';
import TextScore from '../../components/TextScore';
import { sentimentScoreToPercentage } from '../../utils/stringUtils';

interface SentimentSectionProps {
  id?: string;
  ownSentiment: QueryResultDetailedSentimentsItem | undefined;
  competitorSentiment: QueryResultDetailedSentimentsItem | undefined;
}

const SentimentPanel = ({
  title,
  sentiment,
}: {
  title: string;
  sentiment: QueryResultDetailedSentimentsItem;
}) => {
  const percentage = sentimentScoreToPercentage(sentiment.score);
  const brandName = sentiment.brands.map((b) => b.name).join(', ');
  const positiveKeywords = sentiment.keywords
    .filter((k) => k.isPositive)
    .map((k) => k.keyword);
  const negativeKeywords = sentiment.keywords
    .filter((k) => k.isNegative)
    .map((k) => k.keyword);

  return (
    <div className="yst-rounded-lg yst-border yst-border-gray-300 yst-p-4 yst-space-y-4">
      <Title as="h4" size="4">
        {title}
      </Title>

      {brandName && <p className="yst-text-sm">{brandName}</p>}

      <div>
        <div className="yst-inline-flex">
          <TextScore
            className="yst-mb-1.5"
            score={percentage}
            scoreFormat="percentage"
            suffix="positive"
          />
        </div>
        <ProgressBar
          max={100}
          min={0}
          progress={percentage}
          className={cx(
            'yst-bg-red-500',
            '[&>*]:yst-bg-green-500',
          )}
        />
      </div>

      {positiveKeywords.length > 0 && (
        <div className="yst-space-y-2">
          <Title as="h5" size="5">
            Positive keywords
          </Title>
          <div className="yst-flex yst-flex-wrap yst-gap-2">
            {positiveKeywords.map((keyword, index) => (
              <Badge key={index} className="yst-bg-green-100 yst-text-green-800">
                {keyword}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {negativeKeywords.length > 0 && (
        <div className="yst-space-y-2">
          <Title as="h5" size="5">
            Negative keywords
          </Title>
          <div className="yst-flex yst-flex-wrap yst-gap-2">
            {negativeKeywords.map((keyword, index) => (
              <Badge key={index} className="yst-bg-red-100 yst-text-red-800">
                {keyword}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SentimentSection = ({
  id,
  ownSentiment,
  competitorSentiment,
}: SentimentSectionProps) => {
  if (!ownSentiment && !competitorSentiment) {
    return null;
  }

  return (
    <details id={id} className="yst-group/sentiment yst-mt-6" open>
      <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden yst-mb-4">
        <h3 className="yst-font-semibold">Sentiment Analysis</h3>
        <ChevronDownIcon className="yst-h-4 yst-w-4 yst-text-gray-400 yst-flex-shrink-0 group-open/sentiment:yst-rotate-180 yst-transition-transform" />
      </summary>
      <div className="yst-grid yst-grid-cols-1 md:yst-grid-cols-2 yst-gap-4">
        {ownSentiment && (
          <SentimentPanel title="Brand sentiment" sentiment={ownSentiment} />
        )}
        {competitorSentiment && (
          <SentimentPanel
            title="Competitor sentiment"
            sentiment={competitorSentiment}
          />
        )}
      </div>
    </details>
  );
};

export default SentimentSection;
